import { useEffect, useState, type ReactNode } from "react";

export type TocItem = { id: string; label: ReactNode };

/**
 * "On this page": a link to each section. On a wide screen it stays beside the
 * article and marks the section being read. On a phone it is a closed list
 * under the byline that opens with a tap.
 */
export function Toc({ items }: { items: readonly TocItem[] }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<string | null>(items[0]?.id ?? null);

  useEffect(() => {
    let frame = 0;
    // The section being read is the last one whose heading has passed the top quarter of the
    // screen, or the first section before any has.
    const update = () => {
      frame = 0;
      const line = window.innerHeight * 0.25;
      let found: string | null = items[0]?.id ?? null;
      for (const item of items) {
        const heading = document.getElementById(item.id);
        if (heading && heading.getBoundingClientRect().top <= line) found = item.id;
      }
      setCurrent(found);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items]);

  return (
    <nav className="art-toc" aria-label="On this page" data-open={open ? "" : undefined}>
      <p className="art-toc-title art-label">On this page</p>
      <button
        type="button"
        className="art-toc-toggle"
        aria-expanded={open}
        aria-controls="art-toc-list"
        onClick={() => setOpen((was) => !was)}
      >
        <span className="art-label">On this page</span>
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <ol className="art-toc-list" id="art-toc-list">
        {items.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              aria-current={current === item.id ? "location" : undefined}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
